import React, { useState, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { assets } from '../../assets';
import { Paths } from '../../navigation/paths';
import ProfileCircle from './menu-sidebar/components/profile-circle';
import CustomMenu from './menu-sidebar/custom-menu';
import { useSelector } from 'react-redux';
import Auth from '../../logic/redux/auth';
import { useOnClickOutside } from '../../helpers/use-on-click-outside';
import LogoutButton from '../utils/logout-button';
import { withErrorBoundary } from '@sentry/react';
import { Fallback } from '../utils/error-boundary';

const growAnimation = keyframes`
  from {
    max-height: 0px;
  }
  to {
    max-height: 330px;
  }
`;

const MenuDrawer = styled.div`
  width: 100%;
  max-height: 330px;
  animation: ${growAnimation} 0.3s ease-out 0s 1;
  overflow-y: auto;
  position: absolute;
  top: 0px;
  left: 0px;
  transform: translate(0, 60px);
  z-index: 1;
  background-color: inherit;
`;

const Navbar = styled.nav`
  width: 100%;
  background-color: var(--main-bg-color);
  position: relative;
`;

const NavbarHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
  height: 60px;
`;

const profileSubroutes = [
  Paths.profile,
  Paths.notifications,
  Paths.personal_data,
  Paths.settings,
  Paths.login_settings,
  Paths.logout_settings,
  Paths.password_change,
];

const HamburgerButton = (props: { onClick?: () => void }): JSX.Element => {
  return (
    <button onClick={props.onClick}>
      <svg
        width="24"
        height="16"
        viewBox="0 0 24 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title>Menu</title>
        <rect width="24" height="2" fill="#C4C4C4" />
        <rect y="7" width="24" height="2" fill="#C4C4C4" />
        <rect y="14" width="24" height="2" fill="#C4C4C4" />
      </svg>
    </button>
  );
};

const MobileMenu = (): JSX.Element | null => {
  const location = useLocation();
  const history = useHistory();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const visible = useSelector(Auth.select.selectLoggedIn);

  const isActive = profileSubroutes.some((r) => r === location.pathname);

  const menuRef = useRef(null);
  const handleOutsideClick = (): void => {
    if (menuOpen) {
      setMenuOpen(false);
    }
  };
  useOnClickOutside(menuRef, handleOutsideClick);

  useEffect(() => {
    if (menuOpen) {
      setMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleProfileClick = (): void => {
    history.push(Paths.profile);
  };

  if (!visible) return null;

  return (
    <>
      <Navbar ref={menuRef}>
        <NavbarHeader>
          <HamburgerButton onClick={() => setMenuOpen(!menuOpen)} />
          <img src={assets.icons.tradebull_logo} width={140} alt="TradeBull logo" />
          <ProfileCircle isActive={isActive} onClick={handleProfileClick} small />
        </NavbarHeader>
        {menuOpen ? (
          <MenuDrawer>
            <CustomMenu />
            <LogoutButton />
          </MenuDrawer>
        ) : null}
      </Navbar>
    </>
  );
};

export default withErrorBoundary(MobileMenu, { fallback: Fallback });
